<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useInviteStore } from '../../stores/inviteStore'
import { useBandStore } from '../../stores/bandStore'
import { useUserStore } from '../../stores/userStore'
import type { BandMemberDto } from '../../dtos'

const inviteStore = useInviteStore()
const bandStore = useBandStore()
const userStore = useUserStore()

const visible = defineModel<boolean>('visible')
const activeTab = ref('members')
const searchQuery = ref('')
const inviteSentIds = ref<number[]>([])
const errorMessage = ref('')
const savingUserId = ref<number | null>(null)
const showRemoveConfirm = ref(false)
const memberToRemove = ref<BandMemberDto | null>(null)
const removingMember = ref(false)

// Debounced band role updates per member
const roleTimeouts = new Map<number, any>()
const localRoles = ref<Record<number, string>>({})

const isOwner = computed(() => {
  if (!bandStore.currentBand || !userStore.dbUser) return false
  const me = bandStore.currentBand.members.find(m => m.userId === userStore.dbUser!.id)
  return me?.role === 'Owner'
})

const nonOwnerMembers = computed(() => {
  if (!bandStore.currentBand) return []
  return bandStore.currentBand.members.filter(m => m.role !== 'Owner')
})

const ownerMember = computed(() => {
  if (!bandStore.currentBand) return null
  return bandStore.currentBand.members.find(m => m.role === 'Owner') || null
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Debounced search
let searchTimeout: any = null
watch(searchQuery, (newQuery) => {
  if (searchTimeout) clearTimeout(searchTimeout)

  if (newQuery.length < 1) {
    inviteStore.searchResults = []
    return
  }

  searchTimeout = setTimeout(async () => {
    await inviteStore.searchUsers(newQuery)
  }, 300)
})

const sendInvite = async (userId: number) => {
  errorMessage.value = ''
  try {
    await inviteStore.sendInvite(userId)
    inviteSentIds.value.push(userId)
  } catch (error: any) {
    errorMessage.value = error.response?.data || "Failed to send invite."
  }
}

const getLocalRole = (member: BandMemberDto) => {
  return localRoles.value[member.userId] ?? member.bandRole ?? ''
}

const handleRoleChange = (member: BandMemberDto, value: string) => {
  localRoles.value[member.userId] = value
  if (roleTimeouts.has(member.userId)) {
    clearTimeout(roleTimeouts.get(member.userId))
  }
  roleTimeouts.set(member.userId, setTimeout(() => {
    saveMemberField(member.userId, { bandRole: value })
  }, 500))
}

const handleToggle = async (member: BandMemberDto, field: 'canEditSongs' | 'canEditMixes' | 'canEditSetlists' | 'canEditEvents', value: boolean) => {
  await saveMemberField(member.userId, { [field]: value })
}

const saveMemberField = async (userId: number, data: Record<string, any>) => {
  if (!bandStore.currentBand) return
  savingUserId.value = userId
  try {
    await bandStore.updateMember(bandStore.currentBand.id, userId, data)
  } catch (error) {
    console.error('Failed to update member:', error)
  } finally {
    savingUserId.value = null
  }
}

const confirmRemoveMember = (member: BandMemberDto) => {
  memberToRemove.value = member
  showRemoveConfirm.value = true
}

const handleRemoveMember = async () => {
  if (!memberToRemove.value || !bandStore.currentBand) return
  removingMember.value = true
  try {
    await bandStore.removeMember(bandStore.currentBand.id, memberToRemove.value.userId)
    showRemoveConfirm.value = false
    memberToRemove.value = null
  } catch (error) {
    console.error('Failed to remove member:', error)
  } finally {
    removingMember.value = false
  }
}

watch(visible, async (newVal) => {
  if (newVal && bandStore.currentBand) {
    activeTab.value = 'members'
    localRoles.value = {}
    await inviteStore.fetchBandInvites(bandStore.currentBand.id)
  } else {
    searchQuery.value = ''
    inviteStore.searchResults = []
    inviteSentIds.value = []
    errorMessage.value = ''
    localRoles.value = {}
  }
})
</script>

<template>
  <Dialog v-model:visible="visible" modal header="Manage Members" :style="{ width: '34rem' }">
    <div class="flex flex-col gap-4">
      <!-- Tabs -->
      <div class="flex bg-slate-100 p-1 rounded-xl">
        <button class="flex-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-all"
                :class="activeTab === 'members' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                @click="activeTab = 'members'">MEMBERS</button>
        <button class="flex-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-all"
                :class="activeTab === 'invite' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'"
                @click="activeTab = 'invite'">INVITE</button>
      </div>

      <!-- Members Tab -->
      <div v-if="activeTab === 'members'" class="flex flex-col gap-3">
        <!-- Owner -->
        <div v-if="ownerMember" class="p-3 bg-slate-50 border border-slate-400 rounded-lg space-y-3">
          <div class="flex items-center gap-3">
            <Avatar v-if="ownerMember.profilePicture" :image="ownerMember.profilePicture" shape="circle" size="small" />
            <Avatar v-else :label="(ownerMember.displayName || '?').charAt(0)" shape="circle" size="small" severity="primary" />
            <div class="flex-1">
              <p class="text-sm font-bold text-slate-900">{{ ownerMember.displayName || 'Unknown' }}</p>
            </div>
            <i v-if="savingUserId === ownerMember.userId" class="pi pi-spin pi-spinner text-xs text-slate-400"></i>
            <span class="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-amber-100 text-amber-700">Owner</span>
          </div>
          <div v-if="isOwner" class="flex flex-col gap-1">
            <label class="text-[10px] font-bold text-slate-400 uppercase">Role</label>
            <InputText :modelValue="getLocalRole(ownerMember!)"
                       @update:modelValue="(v: string | undefined) => handleRoleChange(ownerMember!, v ?? '')"
                       placeholder="e.g. Drummer, Lead Vocal, Producer"
                       class="w-full !text-xs" size="small" />
          </div>
        </div>

        <!-- Editable Members -->
        <div v-if="nonOwnerMembers.length === 0" class="py-6 text-center text-sm text-slate-400 italic">
          No other members yet.
        </div>
        <div v-for="member in nonOwnerMembers" :key="member.userId"
             class="p-3 bg-slate-50 border border-slate-400 rounded-lg space-y-3">
          <div class="flex items-center gap-3">
            <Avatar v-if="member.profilePicture" :image="member.profilePicture" shape="circle" size="small" />
            <Avatar v-else :label="(member.displayName || '?').charAt(0)" shape="circle" size="small" severity="primary" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-slate-900">{{ member.displayName || 'Unknown' }}</p>
            </div>
            <i v-if="savingUserId === member.userId" class="pi pi-spin pi-spinner text-xs text-slate-400"></i>
            <Button v-if="isOwner" icon="pi pi-trash" severity="danger" text rounded size="small"
                    class="opacity-60 hover:opacity-100 transition-opacity"
                    v-tooltip.top="'Remove member'"
                    @click="confirmRemoveMember(member)" />
          </div>

          <!-- Band Role -->
          <div class="flex flex-col gap-1">
            <label class="text-[10px] font-bold text-slate-400 uppercase">Role</label>
            <InputText :modelValue="getLocalRole(member)"
                       @update:modelValue="(v: string | undefined) => handleRoleChange(member, v ?? '')"
                       placeholder="e.g. Drummer, Lead Vocal, Producer"
                       class="w-full !text-xs" size="small"
                       :disabled="!isOwner" />
          </div>

          <!-- Permission Toggles -->
          <div v-if="isOwner" class="grid grid-cols-2 gap-x-6 gap-y-2">
            <div class="flex items-center gap-2">
              <ToggleSwitch :modelValue="member.canEditSongs"
                         @update:modelValue="(v: boolean) => handleToggle(member, 'canEditSongs', v)" />
              <span class="text-xs text-slate-600">Song Edits</span>
            </div>
            <div class="flex items-center gap-2">
              <ToggleSwitch :modelValue="member.canEditMixes"
                         @update:modelValue="(v: boolean) => handleToggle(member, 'canEditMixes', v)" />
              <span class="text-xs text-slate-600">Mix Edits</span>
            </div>
            <div class="flex items-center gap-2">
              <ToggleSwitch :modelValue="member.canEditSetlists"
                         @update:modelValue="(v: boolean) => handleToggle(member, 'canEditSetlists', v)" />
              <span class="text-xs text-slate-600">Setlist Edits</span>
            </div>
            <div class="flex items-center gap-2">
              <ToggleSwitch :modelValue="member.canEditEvents"
                         @update:modelValue="(v: boolean) => handleToggle(member, 'canEditEvents', v)" />
              <span class="text-xs text-slate-600">Event Edits</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Invite Tab -->
      <div v-if="activeTab === 'invite'" class="flex flex-col gap-4">
        <p class="text-sm text-slate-500">Search for musicians to join <span class="font-bold text-slate-900">{{ bandStore.currentBand?.name }}</span>.</p>

        <div v-if="errorMessage" class="p-3 text-sm font-bold text-amber-500 bg-red-50 rounded-lg border border-red-200">
          {{ errorMessage }}
        </div>

        <div class="flex flex-col gap-2">
          <label class="rd-label">Search Musicians</label>
          <IconField>
            <InputIcon class="pi pi-search" />
            <InputText v-model="searchQuery" placeholder="Start typing a name..." fluid />
            <InputIcon v-if="inviteStore.loading" class="pi pi-spin pi-spinner" />
          </IconField>
        </div>

        <!-- Fixed Results Panel -->
        <div class="h-52 overflow-y-auto border border-slate-100 rounded-lg shadow-inner bg-slate-50/50 flex flex-col">
          <div v-if="inviteStore.searchResults.length > 0">
            <div v-for="user in inviteStore.searchResults" :key="user.id"
                 class="p-3 flex items-center justify-between hover:bg-white border-b border-slate-50 last:border-0 transition-colors">
              <div class="flex items-center gap-3">
                <Avatar v-if="user.profilePicture" :image="user.profilePicture" shape="circle" />
                <Avatar v-else :label="(user.displayName || '?').charAt(0)" shape="circle" severity="primary" />
                <div>
                  <p class="text-sm font-bold text-slate-900">{{ user.displayName || 'Musician' }}</p>
                </div>
              </div>

              <Button v-if="inviteSentIds.includes(user.id)" icon="pi pi-check" severity="success" text disabled size="small" />
              <Button v-else label="Invite" icon="pi pi-plus" size="small" outlined @click="sendInvite(user.id)" />
            </div>
          </div>
          <div v-else class="flex-1 flex flex-col items-center justify-center p-8 text-slate-400">
            <i class="pi pi-users text-4xl mb-3 opacity-20"></i>
            <p v-if="searchQuery.length > 0 && !inviteStore.loading" class="text-sm italic text-center">
              No users found matching "{{ searchQuery }}"
            </p>
            <p v-else-if="searchQuery.length === 0" class="text-sm italic text-center">
              Type to find musicians
            </p>
          </div>
        </div>

        <!-- Pending Invites List -->
        <div v-if="inviteStore.bandInvites.length > 0">
          <label class="rd-label mb-2 block">Pending Invitations</label>
          <div class="flex flex-col gap-2 max-h-32 overflow-y-auto">
            <div v-for="invite in inviteStore.bandInvites" :key="invite.id"
                 class="flex items-center justify-between p-2 bg-slate-50 border border-slate-100 rounded-lg">
              <div class="flex flex-col">
                <span class="text-xs font-bold text-slate-900">{{ invite.recipientName || 'Unknown' }}</span>
                <span class="text-[10px] text-slate-400">Sent {{ formatDate(invite.createdAt) }}</span>
              </div>
              <span class="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-bold border border-amber-100 uppercase">Pending</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Remove Member Confirmation Dialog -->
    <Dialog v-model:visible="showRemoveConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-user-minus text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-4">Remove Member?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will remove <strong>{{ memberToRemove?.displayName || 'this member' }}</strong> from <strong>{{ bandStore.currentBand?.name }}</strong>.</p>
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Remove" severity="danger" class="w-full py-3 font-bold" :loading="removingMember" @click="handleRemoveMember" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showRemoveConfirm = false" />
        </div>
      </div>
    </Dialog>
  </Dialog>
</template>

<style scoped>
:deep(.p-toggleswitch:not(.p-toggleswitch-checked) .p-toggleswitch-slider) {
  background: #94a3b8;
}
:deep(.p-toggleswitch:not(.p-toggleswitch-checked):not(.p-disabled):hover .p-toggleswitch-slider) {
  background: #cbd5e1;
}
:deep(.p-toggleswitch.p-toggleswitch-checked:not(.p-disabled):hover .p-toggleswitch-slider) {
  background: #5eead4 !important;
}
</style>
